import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Loader2, ClipboardPaste, Scale, ChevronRight, Building2, Hash, Calendar, Globe, UserCheck, AlertCircle, Crosshair, Gavel } from "lucide-react";
import { toast } from "sonner";

const HOTSPOT_DISTRICTS = [
  { state: "New York", districts: ["S.D.N.Y. (Manhattan/Bronx)", "E.D.N.Y. (Brooklyn/Queens/L.I.)"], heat: "EXTREME" },
  { state: "California", districts: ["C.D. Cal. (Los Angeles)", "N.D. Cal. (San Francisco/Bay Area)"], heat: "HIGH" },
  { state: "Florida", districts: ["S.D. Fla. (Miami)", "M.D. Fla. (Orlando/Tampa)"], heat: "HIGH" },
];

function heatColor(level) {
  if (level === "EXTREME") return "bg-red-600 text-white";
  if (level === "HIGH") return "bg-amber-500 text-zinc-950";
  return "bg-zinc-200 text-zinc-700";
}

export default function PlaybookAndScratchpad({ onLawsuitAdded }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [matchedFields, setMatchedFields] = useState([]);
  const [missingFields, setMissingFields] = useState([]);
  const [saving, setSaving] = useState(false);

  const onParse = async (e) => {
    e?.preventDefault();
    if (!text.trim()) {
      toast.error("Paste docket text first");
      return;
    }
    setParsing(true);
    try {
      const r = await api.post("/v1/lawsuits/parse-scratchpad", { text });
      const d = r.data;
      const matched = d.matched_fields || [];
      const domain = d.target_website_domain;
      setForm({
        case_number: d.case_number || "",
        filing_date: d.filing_date || "",
        business_name: d.defendant || "",
        plaintiff: d.plaintiff || "",
        website_url: domain ? (domain.startsWith("http") ? domain : `https://${domain}`) : "",
        plaintiff_law_firm: d.plaintiff_law_firm || "",
        jurisdiction: d.jurisdiction || "",
        cause_of_action: d.cause_of_action || "",
        zip_code: d.defendant_zip_code || "",
        niche: "",
        risk_score: 75,
        notes: "",
      });
      setMatchedFields(matched);
      setMissingFields([]);
      setOpen(true);
      if (matched.length === 0) {
        toast.warning("No fields detected — please review and fill manually");
      } else {
        toast.success(`Parsed ${matched.length} field${matched.length === 1 ? "" : "s"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Parsing failed");
    } finally {
      setParsing(false);
    }
  };

  const REQUIRED_FIELDS = [
    { key: "case_number", label: "Case Number" },
    { key: "jurisdiction", label: "Jurisdiction" },
    { key: "filing_date", label: "Filing Date" },
    { key: "plaintiff_law_firm", label: "Plaintiff Law Firm" },
    { key: "business_name", label: "Defendant / Business Name" },
  ];

  const _persistLawsuit = async () => {
    if (!form) return null;
    const missing = REQUIRED_FIELDS.filter((f) => !(form[f.key] || "").toString().trim());
    if (missing.length > 0) {
      setMissingFields(missing.map((f) => f.key));
      toast.error(`Please fill: ${missing.map((f) => f.label).join(", ")}`, { duration: 5000 });
      return null;
    }
    const r = await api.post("/lawsuits", {
      ...form,
      risk_score: parseInt(form.risk_score || 50, 10),
      auto_pipeline: true,
    });
    return r.data;
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const saved = await _persistLawsuit();
      if (!saved) return;
      toast.success(`Lawsuit ${saved.case_number} added · automated pipeline started`);
      setOpen(false);
      setText("");
      setForm(null);
      setMatchedFields([]);
      setMissingFields([]);
      onLawsuitAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onSaveAndScan = async () => {
    setSaving(true);
    try {
      const saved = await _persistLawsuit();
      if (!saved) return;
      const zip = (form.zip_code || saved.zip_code || "").toString().trim();
      const params = new URLSearchParams({ lawsuit: saved.id });
      if (zip) params.set("zip", zip);
      else if (form.website_url) params.set("site", form.website_url);
      toast.success(`Saved · launching Geo-Blitz with neighbor warning attached`);
      setOpen(false);
      setText("");
      setForm(null);
      setMatchedFields([]);
      setMissingFields([]);
      onLawsuitAdded?.();
      navigate(`/geo-blitz?${params.toString()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isMatched = (key) => matchedFields.includes(key);
  const isMissing = (key) => missingFields.includes(key);

  const updateForm = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (missingFields.includes(key) && (value || "").toString().trim()) {
      setMissingFields((m) => m.filter((k) => k !== key));
    }
  };

  return (
    <div className="mb-8" data-testid="playbook-scratchpad">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4">
        {/* ───── Playbook Directory ───── */}
        <div className="border-2 border-zinc-950 bg-white" data-testid="playbook-card">
          <div className="bg-zinc-950 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4" strokeWidth={2.5} />
              <span className="label-xs text-white">National Court Sourcing Playbook</span>
            </div>
            <span className="font-mono text-[10px] text-zinc-400">v1 · nationwide</span>
          </div>

          <div className="p-5 space-y-5">
            {/* Target Portal */}
            <div>
              <div className="label-xs mb-1">Target Portal</div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-2xl md:text-3xl font-black tracking-tighter">PACER</span>
                <span className="text-sm font-mono text-zinc-700">Case Locator (PCL)</span>
              </div>
              <a
                href="https://pcl.uscourts.gov/pcl/index.jsf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 hover:underline mt-1"
                data-testid="playbook-pacer-link"
              >
                pcl.uscourts.gov <ChevronRight className="w-3 h-3" />
              </a>
            </div>

            {/* Search parameter */}
            <div className="border-l-4 border-zinc-950 pl-3">
              <div className="label-xs mb-1">Search Parameter</div>
              <p className="text-sm leading-snug">
                Run a <strong>Civil</strong> search filtered by{" "}
                <strong>Nature of Suit (NOS) Code </strong>
                <span className="inline-block px-2 py-0.5 bg-red-600 text-white font-mono font-black text-xs">446</span>{" "}
                — <em>Civil Rights – Accommodations</em>. This is the canonical NOS used
                for ADA digital accessibility filings.
              </p>
            </div>

            {/* Hotspots */}
            <div>
              <div className="label-xs mb-2">Hotspot Focus Districts</div>
              <ul className="space-y-2">
                {HOTSPOT_DISTRICTS.map((h) => (
                  <li key={h.state} className="flex items-start gap-3" data-testid={`hotspot-${h.state.replace(" ", "-").toLowerCase()}`}>
                    <span className={`shrink-0 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest font-mono ${heatColor(h.heat)}`}>
                      {h.heat}
                    </span>
                    <div className="min-w-0">
                      <div className="font-bold text-sm text-zinc-950">{h.state}</div>
                      <div className="text-xs text-zinc-600 font-mono leading-snug">
                        {h.districts.join(" · ")}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ───── Scratchpad ───── */}
        <div className="border-2 border-zinc-950 bg-white" data-testid="scratchpad-card">
          <div className="bg-zinc-950 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardPaste className="w-4 h-4" strokeWidth={2.5} />
              <span className="label-xs text-white">Smart Ingest Scratchpad</span>
            </div>
            <span className="font-mono text-[10px] text-zinc-400">regex parser · all districts</span>
          </div>

          <form onSubmit={onParse} className="p-5 space-y-3">
            <Label className="label-xs">Paste raw docket text (PACER / ECF / RECAP / state mirror)</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Example:\nCase Number: 1:24-cv-08412\nDate Filed: 11/14/2024\nJANE DOE, Plaintiff,\n          v.\nHUDSON VALLEY BAKERY LLC, Defendant.\nDefendant's website www.hudsonvalleybakery.com ...`}
              rows={9}
              className="rounded-sm font-mono text-xs leading-snug border-zinc-300 resize-none"
              data-testid="scratchpad-textarea"
            />
            <div className="flex justify-between items-center pt-1">
              <span className="font-mono text-[10px] text-zinc-500">
                {text.length} char{text.length === 1 ? "" : "s"}
              </span>
              <Button
                type="submit"
                disabled={parsing || !text.trim()}
                className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider h-10"
                data-testid="scratchpad-parse-button"
              >
                {parsing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing…</>
                ) : (
                  <><ClipboardPaste className="w-4 h-4 mr-2" /> Parse & Pre-Fill</>
                )}
              </Button>
            </div>
            <p className="text-[11px] text-zinc-500 leading-snug">
              The parser dynamically extracts case number, filing date, plaintiff, defendant
              and target domain regardless of court layout, then opens a pre-filled lawsuit
              import form below.
            </p>
          </form>
        </div>
      </div>

      {/* ───── Pre-filled Import Dialog ───── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm border-2 border-zinc-950 p-0 bg-white" data-testid="parsed-dialog">
          <DialogHeader className="bg-zinc-950 text-white px-5 py-3">
            <DialogTitle className="font-display font-black tracking-tight text-lg flex items-center gap-2 text-white">
              <UserCheck className="w-5 h-5" /> Confirm Pre-Filled Lawsuit
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-400">
              {matchedFields.length} field{matchedFields.length === 1 ? "" : "s"} extracted by the regex parser. Edit anything below and click <strong>Save</strong> to add to the radar.
            </DialogDescription>
          </DialogHeader>

          {form && (
            <div className="px-5 py-4 max-h-[70vh] overflow-y-auto space-y-3">
              {missingFields.length > 0 && (
                <div
                  className="flex items-start gap-2 bg-red-50 border-2 border-red-600 px-3 py-2 text-xs text-red-900"
                  data-testid="dialog-missing-fields-banner"
                >
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-700" />
                  <div>
                    <strong className="uppercase tracking-wider text-[10px]">Fill the highlighted fields to continue:</strong>{" "}
                    {REQUIRED_FIELDS.filter((f) => missingFields.includes(f.key))
                      .map((f) => f.label)
                      .join(", ")}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FormField
                  label="Case Number"
                  icon={<Hash className="w-3.5 h-3.5" />}
                  matched={isMatched("case_number")}
                  missing={isMissing("case_number")}
                  testid="dialog-case-number"
                >
                  <Input
                    value={form.case_number}
                    onChange={(e) => updateForm("case_number", e.target.value)}
                    placeholder="1:24-cv-08412"
                    className={`rounded-sm font-mono text-sm ${isMissing("case_number") ? "border-red-600 ring-1 ring-red-500" : ""}`}
                    data-testid="dialog-case-number-input"
                  />
                </FormField>

                <FormField
                  label="Filing Date"
                  icon={<Calendar className="w-3.5 h-3.5" />}
                  matched={isMatched("filing_date")}
                  missing={isMissing("filing_date")}
                  testid="dialog-filing-date"
                >
                  <Input
                    type="date"
                    value={form.filing_date}
                    onChange={(e) => updateForm("filing_date", e.target.value)}
                    className={`rounded-sm font-mono text-sm ${isMissing("filing_date") ? "border-red-600 ring-1 ring-red-500" : ""}`}
                    data-testid="dialog-filing-date-input"
                  />
                </FormField>

                <FormField
                  label="Plaintiff"
                  icon={<UserCheck className="w-3.5 h-3.5" />}
                  matched={isMatched("plaintiff")}
                  testid="dialog-plaintiff"
                >
                  <Input
                    value={form.plaintiff || ""}
                    onChange={(e) => updateForm("plaintiff", e.target.value)}
                    placeholder="Jane Doe"
                    className="rounded-sm text-sm"
                    data-testid="dialog-plaintiff-input"
                  />
                </FormField>

                <FormField
                  label="Defendant / Business Name"
                  icon={<Building2 className="w-3.5 h-3.5" />}
                  matched={isMatched("defendant")}
                  missing={isMissing("business_name")}
                  testid="dialog-business-name"
                >
                  <Input
                    value={form.business_name}
                    onChange={(e) => updateForm("business_name", e.target.value)}
                    placeholder="Acme Bakery LLC"
                    className={`rounded-sm text-sm ${isMissing("business_name") ? "border-red-600 ring-1 ring-red-500" : ""}`}
                    data-testid="dialog-business-name-input"
                  />
                </FormField>

                <FormField
                  label="Target Website"
                  icon={<Globe className="w-3.5 h-3.5" />}
                  matched={isMatched("target_website_domain")}
                  testid="dialog-website"
                >
                  <Input
                    value={form.website_url}
                    onChange={(e) => updateForm("website_url", e.target.value)}
                    placeholder="https://acmebakery.com"
                    className="rounded-sm font-mono text-sm"
                    data-testid="dialog-website-input"
                  />
                </FormField>

                <FormField
                  label="Jurisdiction (required)"
                  matched={isMatched("jurisdiction")}
                  missing={isMissing("jurisdiction")}
                  testid="dialog-jurisdiction"
                >
                  <Input
                    value={form.jurisdiction}
                    onChange={(e) => updateForm("jurisdiction", e.target.value)}
                    placeholder="S.D.N.Y."
                    className={`rounded-sm text-sm ${isMissing("jurisdiction") ? "border-red-600 ring-1 ring-red-500" : ""}`}
                    data-testid="dialog-jurisdiction-input"
                  />
                </FormField>

                <FormField
                  label="Plaintiff Law Firm (required)"
                  matched={isMatched("plaintiff_law_firm")}
                  missing={isMissing("plaintiff_law_firm")}
                  testid="dialog-firm"
                >
                  <Input
                    value={form.plaintiff_law_firm}
                    onChange={(e) => updateForm("plaintiff_law_firm", e.target.value)}
                    placeholder="Mizrahi Kroub LLP"
                    className={`rounded-sm text-sm ${isMissing("plaintiff_law_firm") ? "border-red-600 ring-1 ring-red-500" : ""}`}
                    data-testid="dialog-firm-input"
                  />
                </FormField>

                <FormField
                  label="Zip Code"
                  testid="dialog-zip"
                  matched={isMatched("defendant_zip_code")}
                >
                  <Input
                    value={form.zip_code}
                    onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                    placeholder="10001"
                    maxLength={10}
                    className="rounded-sm font-mono text-sm"
                    data-testid="dialog-zip-input"
                  />
                </FormField>

                <FormField label="Niche" testid="dialog-niche">
                  <Input
                    value={form.niche}
                    onChange={(e) => setForm({ ...form, niche: e.target.value })}
                    placeholder="Food & Beverage"
                    className="rounded-sm text-sm"
                    data-testid="dialog-niche-input"
                  />
                </FormField>

                <FormField label={`Risk Score: ${form.risk_score}`} testid="dialog-risk">
                  <Input
                    type="range"
                    min={0}
                    max={100}
                    value={form.risk_score}
                    onChange={(e) => setForm({ ...form, risk_score: e.target.value })}
                    className="rounded-sm h-2 mt-3 cursor-pointer"
                    data-testid="dialog-risk-input"
                  />
                </FormField>

                <FormField
                  label="Cause of Action"
                  icon={<Gavel className="w-3.5 h-3.5" />}
                  matched={isMatched("cause_of_action")}
                  testid="dialog-cause"
                >
                  <Input
                    value={form.cause_of_action}
                    onChange={(e) => setForm({ ...form, cause_of_action: e.target.value })}
                    placeholder="e.g. Violation of Title III of the ADA"
                    className="rounded-sm text-sm"
                    data-testid="dialog-cause-input"
                  />
                </FormField>

                <FormField label="Notes (Plaintiff details, etc.)" testid="dialog-notes" matched={isMatched("plaintiff")}>
                  <Input
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Plaintiff: …"
                    className="rounded-sm text-sm"
                    data-testid="dialog-notes-input"
                  />
                </FormField>
              </div>

              {matchedFields.length === 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    The parser couldn't extract any fields from the pasted text. Fill the form
                    manually or paste a clearer docket excerpt.
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-sm border-zinc-950 font-bold uppercase tracking-wider"
              data-testid="dialog-cancel-button"
            >
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={onSave}
                disabled={saving}
                variant="outline"
                className="rounded-sm border-zinc-950 font-bold uppercase tracking-wider"
                data-testid="dialog-save-button"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  <>Save to Radar</>
                )}
              </Button>
              <Button
                onClick={onSaveAndScan}
                disabled={saving}
                className="rounded-sm bg-red-700 hover:bg-red-800 text-white font-bold uppercase tracking-wider"
                data-testid="dialog-save-and-scan-button"
                title="Save the lawsuit, attach it as the pitch context, and jump to Geo-Blitz with the defendant's ZIP pre-filled"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Working…</>
                ) : (
                  <><Crosshair className="w-4 h-4 mr-2" /> Save & Scan Neighbors</>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, icon, matched, missing, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 text-zinc-700">
          {icon} {label}
        </Label>
        {matched && (
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300 font-mono">
            parsed
          </span>
        )}
        {missing && !matched && (
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white border border-red-700 font-mono">
            required
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
